import React, { useState, useEffect } from 'react';
import { Plus, CreditCard, TrendingUp, ArrowLeftRight, ShoppingBag, Handshake, Sprout, BookOpen, Home, CheckSquare, Ticket, StickyNote, Bookmark, Heart } from 'lucide-react';
import { INVESTMENTS_FEATURE_ICON } from '../../lib/investmentFeatureIcon';
import { CLIENTS_FEATURE_ICON } from '../../lib/clientFeatureIcon';
import { useFinanceStore } from '../../store/useFinanceStore';
import { Dialog, Transition } from '@headlessui/react';
import { TransferModal } from '../Transfers/TransferModal';
import { DPSTransferModal } from '../Transfers/DPSTransferModal';
import { AccountForm } from '../Accounts/AccountForm';
import { TransactionForm } from '../Transactions/TransactionForm';
import { BulkTransactionForm } from '../Transactions/BulkTransactionForm';
import { ClientForm } from '../Clients/ClientForm';
import { TaskForm } from '../Tasks/TaskForm';
import { LendBorrowForm } from '../LendBorrow/LendBorrowForm';
import { HabitForm } from '../Habits/HabitForm';
import { CourseForm } from '../Learning/CourseForm';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { PurchaseForm } from '../Purchases/PurchaseForm';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { useMobileSidebar } from '../../context/MobileSidebarContext';
import { BusinessInvestmentContractModal } from '../Dashboard/BusinessInvestmentContractModal';
import { PrizeBondAddModalHost } from '../PrizeBonds/PrizeBondAddModalHost';
import { investmentsBondsPath } from '../../lib/investmentsNav';
import { PERSONAL_GROWTH_COMPOSE, personalGrowthPath } from '../../lib/personalGrowthNav';
import { insertBookLibraryItem } from '../../lib/bookLibraryService';
import { BookLibraryFormModal } from '../BookLibrary/BookLibraryFormModal';

interface ActionButtonProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  color: string;
  delay: string;
  /** Navigation items (e.g. Go to Dashboard) vs create/action items */
  variant?: 'action' | 'nav';
  /** Icon-only row (label kept for title/aria) */
  iconOnly?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon: Icon, label, onClick, color, delay, variant = 'action', iconOnly = false }) => {
  const isNav = variant === 'nav';
  return (
    <button
      onClick={onClick}
      className={`flex items-center w-full group focus:outline-none ${iconOnly ? 'justify-center' : 'justify-between'}`}
      style={{ transitionDelay: delay }}
      title={label}
      aria-label={label}
    >
      <span className={`${isNav ? 'bg-white/15 ring-1 ring-inset ring-white/30' : color} text-white p-2 sm:p-2.5 rounded-full ${isNav ? '' : 'shadow-md'} flex items-center justify-center ${iconOnly ? '' : 'mr-2 sm:mr-3'} group-hover:scale-110 transition-transform duration-200 flex-shrink-0`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </span>
      {!iconOnly && (
        <span
          className={`px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex-1 min-w-0 text-left duration-150 truncate ${
            isNav
              ? 'border border-white/25 bg-white/10 text-white/95 group-hover:bg-white/15'
              : 'shadow-sm border border-gray-700 group-hover:bg-white/10 dark:group-hover:bg-gray-100/10'
          }`}
          style={isNav ? undefined : { background: '#4c54618f', color: 'white' }}
        >
          {label}
        </span>
      )}
    </button>
  );
};

const partitionActions = (actions: ActionButtonProps[]) => ({
  grid: actions.filter((a) => a.variant !== 'nav'),
  nav: actions.filter((a) => a.variant === 'nav'),
});

export const FloatingActionButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAccountForm, setShowAccountFormLocal] = useState(false);
  const [showTransferTypeModal, setShowTransferTypeModal] = useState(false);
  const [showCurrencyTransferModal, setShowCurrencyTransferModal] = useState(false);
  const [showDpsTransferModal, setShowDpsTransferModal] = useState(false);
  const [showInBetweenTransferModal, setShowInBetweenTransferModal] = useState(false);
  const [showLendBorrowForm, setShowLendBorrowForm] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [showTip, setShowTip] = useState(true);
  const [showBulkTransactionForm, setShowBulkTransactionForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showHabitForm, setShowHabitForm] = useState(false);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showBookForm, setShowBookForm] = useState(false);
  const [showInvestmentContractModal, setShowInvestmentContractModal] = useState(false);
  const [showPrizeBondModal, setShowPrizeBondModal] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const { isMobile } = useMobileDetection();
  const { isMobileSidebarOpen } = useMobileSidebar();
  const { 
    setShowTransactionForm, 
    showTransactionForm, 
    setShowPurchaseForm, 
    showPurchaseForm,
    purchaseFormRecord,
    addLendBorrowRecord
  } = useFinanceStore();
  
  // Memoize categories to prevent infinite re-renders
  const categories = useFinanceStore(React.useCallback(state => state.categories, []));
  const purchaseCategories = useFinanceStore(React.useCallback(state => state.purchaseCategories, []));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  // Check if categories exist and redirect to settings if needed - memoized to prevent infinite re-renders
  const checkCategoriesAndRedirect = React.useCallback((type: 'transaction' | 'purchase') => {



    
    const hasIncomeCategories = categories.filter(cat => cat.type === 'income').length > 0;
    const hasExpenseCategories = purchaseCategories.length > 0; // Use purchaseCategories since transaction form now uses them
    


    
    if (type === 'transaction') {
      if (!hasIncomeCategories || !hasExpenseCategories) {

        toast.error('Please add categories first before creating transactions', {
          description: 'You need both income and expense categories to create transactions.',
          action: {
            label: 'Go to Settings',
            onClick: () => navigate('/settings?tab=categories')
          }
        });
        return false;
      }
    } else if (type === 'purchase') {
      if (!hasExpenseCategories) {

        toast.error('Please add expense categories first before creating purchases', {
          description: 'You need expense categories to create purchases.',
          action: {
            label: 'Go to Settings',
            onClick: () => navigate('/settings?tab=categories')
          }
        });
        return false;
      }
    }

    return true;
  }, [categories, purchaseCategories, navigate]);

  const handleAction = React.useCallback((action: () => void) => {
    action();
    setIsOpen(false);
  }, []);

  const handleAddTransaction = React.useCallback(() => {

    if (checkCategoriesAndRedirect('transaction')) {

      setShowTransactionForm(true);

    }
  }, [checkCategoriesAndRedirect, setShowTransactionForm]);

  const handleAddBulkTransactions = React.useCallback(() => {
    if (checkCategoriesAndRedirect('transaction')) {
      setShowBulkTransactionForm(true);
    }
  }, [checkCategoriesAndRedirect]);

  const handleAddPurchase = React.useCallback(() => {
    if (checkCategoriesAndRedirect('purchase')) {
      setShowPurchaseForm(true);
    }
  }, [checkCategoriesAndRedirect, setShowPurchaseForm]);

  const handleTransfer = React.useCallback(() => {
    setShowTransferTypeModal(true);
    setIsOpen(false);
  }, [setShowTransferTypeModal]);

  const handleSelectTransferType = React.useCallback((type: 'currency' | 'dps' | 'inbetween') => {
    setShowTransferTypeModal(false);
    if (type === 'currency') {
      setShowCurrencyTransferModal(true);
    } else if (type === 'dps') {
      setShowDpsTransferModal(true);
    } else if (type === 'inbetween') {
      setShowInBetweenTransferModal(true);
    }
  }, [setShowTransferTypeModal, setShowCurrencyTransferModal, setShowDpsTransferModal, setShowInBetweenTransferModal]);

  const handleAddLendBorrowRecord = React.useCallback(async (record: any) => {
    try {
      await addLendBorrowRecord(record);
      toast.success('Record added successfully!');
    } catch (error) {

      toast.error('Failed to add record. Please try again.');
    }
  }, [addLendBorrowRecord]);

  const handleAIChat = React.useCallback(() => {
    setShowAIChat(true);
    setIsOpen(false);
  }, []);

  const handleAddHabit = React.useCallback(() => {
    setShowHabitForm(true);
  }, []);

  const handleAddCourse = React.useCallback(() => {
    setShowCourseForm(true);
  }, []);

  const handleAddClientTask = React.useCallback(() => {
    setShowTaskForm(true);
  }, []);

  const handleCourseSuccess = React.useCallback(() => {
    setShowCourseForm(false);
  }, []);

  const handleAddBook = React.useCallback(async (input: Parameters<typeof insertBookLibraryItem>[0]) => {
    try {
      await insertBookLibraryItem(input);
      toast.success('Book added');
      setShowBookForm(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save book');
      throw err;
    }
  }, []);

  const goPersonalGrowth = React.useCallback((tab: string, extra?: Record<string, string>) => {
    navigate(personalGrowthPath(tab, extra));
  }, [navigate]);

  const handleGoHome = React.useCallback(() => {
    if (location.pathname === '/dashboard-demo-only') window.close();
    else navigate('/');
  }, [location.pathname, navigate]);

  /** Nav item (Go to Dashboard / Close demo) is separate from create actions; omitted only on `/` and `/dashboard`. */
  const menu = React.useMemo(() => {
    const financial: ActionButtonProps[] = [
      { label: t('dashboard.addTransaction'), icon: TrendingUp, color: 'bg-blue-600', onClick: () => handleAction(handleAddTransaction), delay: '200ms' },
      { label: 'Add Purchase', icon: ShoppingBag, color: 'bg-orange-600', onClick: () => handleAction(handleAddPurchase), delay: '150ms' },
      { label: t('dashboard.transfer'), icon: ArrowLeftRight, color: 'bg-purple-600', onClick: handleTransfer, delay: '0ms' },
      { label: t('dashboard.addAccount'), icon: CreditCard, color: 'bg-green-600', onClick: () => handleAction(() => setShowAccountFormLocal(true)), delay: '50ms' },
      { label: 'Lent & Borrow', icon: Handshake, color: 'bg-indigo-600', onClick: () => handleAction(() => setShowLendBorrowForm(true)), delay: '100ms' },
      { label: 'Add investment contract', icon: INVESTMENTS_FEATURE_ICON, color: 'bg-sky-600', onClick: () => handleAction(() => setShowInvestmentContractModal(true)), delay: '95ms' },
      { label: t('prizeBond.addBond'), icon: Ticket, color: 'bg-amber-600', onClick: () => handleAction(() => setShowPrizeBondModal(true)), delay: '92ms' },
      { label: 'Add Client', icon: CLIENTS_FEATURE_ICON, color: 'bg-teal-600', onClick: () => handleAction(() => setShowClientForm(true)), delay: '75ms' },
      { label: 'Add Client Task', icon: CheckSquare, color: 'bg-indigo-600', onClick: () => handleAction(handleAddClientTask), delay: '70ms' },
    ];

    const personalGrowth: ActionButtonProps[] = [
      { label: 'Add Habit', icon: Sprout, color: 'bg-emerald-600', onClick: () => handleAction(handleAddHabit), delay: '140ms' },
      { label: 'Add Note', icon: StickyNote, color: 'bg-violet-600', onClick: () => handleAction(() => goPersonalGrowth('notes', { [PERSONAL_GROWTH_COMPOSE]: '1' })), delay: '135ms' },
      { label: 'Add Course', icon: BookOpen, color: 'bg-cyan-600', onClick: () => handleAction(handleAddCourse), delay: '130ms' },
      { label: 'Add Book', icon: Bookmark, color: 'bg-pink-600', onClick: () => handleAction(() => setShowBookForm(true)), delay: '125ms' },
      { label: 'Favorite Quotes', icon: Heart, color: 'bg-rose-600', onClick: () => handleAction(() => goPersonalGrowth('favorite-quotes')), delay: '120ms' },
    ];

    const onDashboard = location.pathname === '/' || location.pathname === '/dashboard';
    const navAction = onDashboard
      ? null
      : {
          label: location.pathname === '/dashboard-demo-only' ? 'Close demo' : 'Go to Dashboard',
          icon: Home,
          color: 'bg-slate-600',
          onClick: () => handleAction(handleGoHome),
          delay: '0ms',
          variant: 'nav' as const,
          iconOnly: true,
        };

    return {
      navAction,
      categories: [
        { category: 'Financial', actions: financial },
        { category: 'Personal Growth', actions: personalGrowth },
      ],
    };
  }, [
    location.pathname,
    t,
    handleAction,
    handleGoHome,
    handleAddTransaction,
    handleAddPurchase,
    handleAddHabit,
    handleAddCourse,
    handleAddClientTask,
    goPersonalGrowth,
    setShowLendBorrowForm,
    setShowInvestmentContractModal,
    setShowPrizeBondModal,
    setShowAccountFormLocal,
    handleTransfer,
    setShowClientForm,
  ]);



  // Load purchase categories when component mounts
  useEffect(() => {
    if (user && purchaseCategories.length === 0) {
      useFinanceStore.getState().fetchPurchaseCategories();
    }
  }, [user, purchaseCategories.length]);

  // Keyboard shortcut: Ctrl+B or Cmd+B to toggle quick button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Show tip only for 5 seconds after page refresh (first load)
  useEffect(() => {
    if (showTip) {
      const timer = setTimeout(() => setShowTip(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showTip]);

  return (
    <>
      <div 
        className={`fixed right-3 sm:right-4 md:right-6 z-50 flex flex-col items-end ${isMobile && isMobileSidebarOpen ? 'hidden' : ''} ${isMobile ? 'browser-fab-positioning' : ''}`}
        style={{ 
          bottom: isMobile ? undefined : `max(1rem, calc(1rem + env(safe-area-inset-bottom, 0px)))`
        }}
      >
        <div className="relative flex flex-col items-end">
          {/* Animated Menu Items - now absolutely positioned above the FAB */}
          <Transition
            show={isOpen}
            enter="transition-all ease-in-out duration-200"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="transition-all ease-in-out duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="absolute bottom-full right-0 mb-2 sm:mb-4 rounded-xl sm:rounded-2xl shadow-2xl z-50 bg-gradient-primary overflow-hidden w-[calc(100vw-3rem)] max-w-[280px] sm:max-w-[440px] sm:min-w-[280px] md:min-w-[360px] lg:min-w-[440px]">
              <div className="max-h-[60vh] sm:max-h-[400px] overflow-y-auto p-3 sm:p-4 flex flex-col gap-2 sm:gap-3 custom-scrollbar">
                {menu.navAction && (
                  <div className="w-full pb-2 mb-0.5 border-b border-white/15">
                    <ActionButton {...menu.navAction} />
                  </div>
                )}
                {menu.categories.map((categoryGroup) => {
                  const { grid, nav } = partitionActions(categoryGroup.actions);
                  return (
                    <div key={categoryGroup.category} className="flex flex-col gap-1.5 sm:gap-2">
                      <div className="text-[10px] sm:text-xs font-semibold text-white/80 uppercase tracking-wider px-1 mb-0.5 sm:mb-1">
                        {categoryGroup.category}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                        {grid.map((action) => (
                          <ActionButton key={action.label} {...action} />
                        ))}
                      </div>
                      {nav.map((action) => (
                        <div key={action.label} className="w-full mt-1 sm:mt-1.5">
                          <ActionButton {...action} />
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </Transition>
          {/* Helper Tip - now left of the button, no background - only show on desktop */}
          <div className="flex items-center">
            {!isOpen && showTip && !isMobile && (
              <span className="mr-2 text-xs text-white bg-gray-800/95 border-2 border-blue-400 px-4 py-2 rounded-lg animate-fade-in z-[100] shadow-lg">
                Tip: Press <b>Ctrl+B</b> (or <b>Cmd+B</b>) to toggle quick actions
                <button onClick={() => setShowTip(false)} className="ml-2 text-gray-300 hover:text-white p-1 rounded transition-colors" aria-label="Dismiss tip">×</button>
              </span>
            )}
            {/* Main FAB Button */}
            <button
              data-tour="quick-actions"
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-3 sm:p-3.5 rounded-full shadow-lg bg-gradient-primary hover:bg-gradient-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus-ring-gradient transition-all"
              aria-label={isOpen ? 'Close actions' : 'Open actions'}
            >
              <Plus className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 ${isOpen ? 'rotate-45' : 'rotate-0'}`} />
            </button>
          </div>
        </div>
      </div>
      {/* Transfer Type Selection Modal */}
      <Dialog open={showTransferTypeModal} onClose={() => setShowTransferTypeModal(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-xs rounded-lg bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
              {t('dashboard.selectTransferType')}
            </Dialog.Title>
            <div className="flex flex-col gap-4">
              <button
                onClick={() => handleSelectTransferType('currency')}
                className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors text-left"
              >
                <div className="font-medium">{t('dashboard.currencyTransfer')}</div>
                <div className="text-sm opacity-90">{t('dashboard.currencyTransferDescription')}</div>
              </button>
              <button
                onClick={() => handleSelectTransferType('dps')}
                className="bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors text-left"
              >
                <div className="font-medium">{t('dashboard.dpsTransfer')}</div>
                <div className="text-sm opacity-90">{t('dashboard.dpsTransferDescription')}</div>
              </button>
              <button
                onClick={() => handleSelectTransferType('inbetween')}
                className="bg-gray-200 text-gray-900 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors text-left"
              >
                <div className="font-medium">In-between Transfer</div>
                <div className="text-sm opacity-90">Transfer between accounts within the same currency</div>
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Modals */}
      {showAccountForm && <AccountForm isOpen={showAccountForm} onClose={() => setShowAccountFormLocal(false)} />}
      {showCurrencyTransferModal && <TransferModal isOpen={showCurrencyTransferModal} onClose={() => setShowCurrencyTransferModal(false)} mode="currency" />}
      {showDpsTransferModal && <DPSTransferModal isOpen={showDpsTransferModal} onClose={() => setShowDpsTransferModal(false)} />}
      {showInBetweenTransferModal && <TransferModal isOpen={showInBetweenTransferModal} onClose={() => setShowInBetweenTransferModal(false)} mode="inbetween" />}
      <TransactionForm isOpen={showTransactionForm} onClose={() => setShowTransactionForm(false)} />
      <BulkTransactionForm isOpen={showBulkTransactionForm} onClose={() => setShowBulkTransactionForm(false)} />
      <PurchaseForm isOpen={showPurchaseForm} record={purchaseFormRecord ?? undefined} onClose={() => setShowPurchaseForm(false)} />

      {showLendBorrowForm && (
        <LendBorrowForm 
          onClose={() => setShowLendBorrowForm(false)} 
          onSubmit={handleAddLendBorrowRecord}
        />
      )}

      {showClientForm && (
        <ClientForm 
          isOpen={showClientForm} 
          onClose={() => setShowClientForm(false)} 
        />
      )}

      {showHabitForm && (
        <HabitForm 
          isOpen={showHabitForm} 
          onClose={() => setShowHabitForm(false)} 
          habit={null}
        />
      )}

      {showCourseForm && (
        <CourseForm 
          course={null}
          onClose={() => setShowCourseForm(false)} 
          onSuccess={handleCourseSuccess}
        />
      )}

      {showBookForm && (
        <BookLibraryFormModal
          open
          book={null}
          onClose={() => setShowBookForm(false)}
          onSubmit={handleAddBook}
        />
      )}

      {showTaskForm && (
        <TaskForm
          isOpen={showTaskForm}
          onClose={() => setShowTaskForm(false)}
        />
      )}

      <BusinessInvestmentContractModal
        open={showInvestmentContractModal}
        onClose={() => setShowInvestmentContractModal(false)}
        onAdded={() => navigate('/investments')}
      />

      <PrizeBondAddModalHost
        open={showPrizeBondModal}
        onClose={() => setShowPrizeBondModal(false)}
        onSuccess={() => navigate(investmentsBondsPath())}
      />

      {/* AI Chat Bot - temporarily hidden, will be enabled later */}
      {/* <AIChatBot 
        isOpen={showAIChat} 
        onOpenChange={setShowAIChat}
        showFloatingButton={false}
      /> */}
    </>
  );
}; 

